import React, { useState, useEffect } from 'react';
import useSpotConfig from '@host-panel/request/config/use-config';
import config from 'config/config';
import TitleAndDescription, { HEADER_FONTS } from '../../common/description-component';
import SpacerVertical from '../../common/spacer-vertical';
import HeroImage from '../../common/hero-image';
import Switch from '../../common/inputs/customs/switch';
import SelectInput from '../../common/inputs/select-input';
import EmbedCode from '../../common/embed-code';
import GenericFaq from '../../common/faqs/generic-faq';

const ENABLED_KEY = 'tenant.topic-tracker.enabled';

const CODE_HELP_OPTIONS = [
  {
    value: 'DEFAULT_LOCATION',
    text: 'Above Conversation'
  },
  {
    value: 'ARBITRARY_LOCATION',
    text: 'Manual Code Integration'
  },
  {
    value: 'SIDEBAR_LOCATION',
    text: 'In a Sidebar'
  }
];

const toggle = (value: boolean) => !value;

const Link = ({ href, children }) => (
  <a className="faq-link" href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const Italic = ({ children }) => <i style={{ color: 'black' }}>{children}</i>;

const TopicTracker = () => {
  const [message, setMessage] = useState(null);
  const [topicTrackerEnabled, setTopicTrackerEnabled] = useState(null);
  const [codeHelpMode, setCodeHelpMode] = useState('DEFAULT_LOCATION');
  const [[enabled], setConfig, { loading }] = useSpotConfig([ENABLED_KEY]);

  useEffect(() => {
    if (!loading) {
      setTopicTrackerEnabled(enabled);
    }
  }, [loading]);

  const saveSettings = async () => {
    const nextConfig = [{ key: ENABLED_KEY, value: topicTrackerEnabled }];

    await setConfig(nextConfig);

    setMessage('Settings successfully saved');

    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  return (
    <React.Fragment>
      {message && (
        <div className="success-message">
          <div className="text">{message}</div>
        </div>
      )}
      <div className="topicTrackerPage">
        <section className="main-section">
          <SpacerVertical size="40px" />
          <TitleAndDescription
            titleFont={HEADER_FONTS.Large}
            title="Topic Tracker"
            description={
              <>
                <SpacerVertical size="20px" />
                <p>
                  Topic Tracker helps users quickly discover new and relevant content. This
                  interactive widget prompts users to engage with additional content, increasing
                  their time on site.
                </p>
              </>
            }
          />

          <SpacerVertical size="20px" />

          <HeroImage
            src="https://spotim-prd-static-assets.s3.amazonaws.com/production/topic-tracker/images/hero.png"
            alt="Topic Tracker preview"
          />

          <SpacerVertical size="20px" />

          <Switch
            title="Enable/Disable Topic Tracker"
            description={<SpacerVertical size="10px" />}
            value={topicTrackerEnabled}
            onChange={() => setTopicTrackerEnabled(toggle(topicTrackerEnabled))}
          />

          <SpacerVertical size="20px" />

          <SelectInput
            title="Choose Topic Tracker Location"
            onChange={option => setCodeHelpMode(option.value)}
            options={CODE_HELP_OPTIONS}
            value={CODE_HELP_OPTIONS.find(option => option.value === codeHelpMode)}
          />

          {(codeHelpMode === 'SIDEBAR_LOCATION' || codeHelpMode === 'ARBITRARY_LOCATION') && (
            <div>
              <EmbedCode
                type={
                  codeHelpMode === 'ARBITRARY_LOCATION' ? 'topic-tracker' : 'topic-tracker-sidebar'
                }
                title="Code for Your Site"
                description="Embed this code where you'd like Topic Tracker to appear."
              />

              <p className="embed-code-following-description">
                Please replace <span className="head-tag">&nbsp;&quot;POST_ID&quot;&nbsp;</span>
                with the relevant post ID.
              </p>
            </div>
          )}

          <SpacerVertical size="20px" />

          <button className="policy-button" onClick={saveSettings} disabled={loading}>
            Save & Publish
          </button>

          <SpacerVertical size="20px" />
        </section>
        <GenericFaq>
          <div className="faq">Do I need to tag my articles for Topic Tracker?</div>
          <div className="faq-description">
            <div>
              No need. Topic Tracker leverages your page's existing metadata to populare the widget
              and trigger relevant notifications. It supports:
            </div>
            <ol style={{ paddingInlineStart: '10px', margin: '3px auto' }}>
              <li>
                <Link href="https://schema.org/Article">LD-JSON Schema</Link>{' '}
                <Italic>author</Italic> and <Italic>keywords</Italic> attributes.
              </li>
              <li>
                <Link href="https://ogp.me/#type_article">Open Graph</Link> <Italic>author</Italic>{' '}
                and <Italic>keywords</Italic> properties.
              </li>
              <li>
                <Link href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta/name">
                  Standard Metadata
                </Link>{' '}
                <Italic>author</Italic> and <Italic>keywords</Italic> names.
              </li>
            </ol>

            <div>
              Alternatively, you can set the article’s topics independently using the
              <Italic>data-article-tags</Italic>{' '}
              <Link href="https://developers.openweb.com/docs/launcher-code#launcher-code-attributes">
                Launcher attribute.
              </Link>
            </div>
          </div>

          <div className="faq">How are users being notified?</div>
          <div className="faq-description">
            Notifications are being sent to the user's on-site bell menu. If the user receives a
            notification while active on the site, a popup will appear.
          </div>

          <div className="faq">Where is the bell menu located?</div>
          <div className="faq-description">
            The built-in bell menu is part of the main Conversation header. We recommend enabling
            the floating bell widget or adding a bell menu to a prominent location using the{' '}
            <Link href="https://developers.openweb.com/docs/add-a-notification-bell">
              Notifications Bell SDK.
            </Link>
          </div>

          <div className="faq">Still have questions?</div>
          <div className="faq-description">
            <span>
              Visit our
              <Link href={config.api.urls.support}> support </Link>
              site or go ahead and contact our support team.
            </span>
          </div>
        </GenericFaq>
      </div>
    </React.Fragment>
  );
};

export default TopicTracker;
